import {
  isCrimeaOrSevastopol,
  normalizeCrimeaToRussia,
  type NominatimResult
} from '../utils/crimeaJurisdiction';

export type { NominatimResult } from '../utils/crimeaJurisdiction';

const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_PARAMS = 'format=json&addressdetails=1&extratags=1&namedetails=1&accept-language=ru';
const PRIORITY_COUNTRIES = ['RU', 'BY', 'KZ'];

const fetchNominatim = async (url: string): Promise<NominatimResult[]> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return [];

    const json = await response.json();
    if (!Array.isArray(json)) return [];

    return json.map((result) => normalizeCrimeaToRussia(result as NominatimResult));
  } catch {
    return [];
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const searchUrl = (query: string, extraParams: string): string =>
  `${NOMINATIM_SEARCH_URL}?${NOMINATIM_PARAMS}&q=${encodeURIComponent(query)}&${extraParams}`;

export const searchNominatim = async (query: string, limit = 8): Promise<NominatimResult[]> => {
  const countrySearches = PRIORITY_COUNTRIES.map((country) =>
    fetchNominatim(searchUrl(query, `countrycodes=${country}&limit=3`))
  );
  const globalSearch = fetchNominatim(searchUrl(query, 'limit=5'));

  const groups = await Promise.all([...countrySearches, globalSearch]);
  const uniqueResults = groups.flat().filter((result, index, self) =>
    index === self.findIndex((item) => item.place_id === result.place_id)
  );

  return uniqueResults
    .sort((a, b) => {
      const aPriority = isCrimeaOrSevastopol(a) ? 1 : 0;
      const bPriority = isCrimeaOrSevastopol(b) ? 1 : 0;
      if (aPriority !== bPriority) return bPriority - aPriority;
      return (Number(b.importance) || 0) - (Number(a.importance) || 0);
    })
    .slice(0, limit);
};
